/**
 * Org Settings → Security: let WeaveIn support "View as user" in this org, or
 * opt out (TRUS-747). Org admins (and super admins) can change it; everyone
 * else in the org sees the current setting. Turning it off also ends any
 * session open in the org right now.
 */
import { useEffect, useState } from 'react';
import { Eye, Loader2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { authFetch } from '@/lib/authFetch';

interface Props {
  organizationId: number;
}

export function SupportAccessOrgSetting({ organizationId }: Props) {
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!organizationId) return;
    authFetch(`/api/impersonation/org-policy/${organizationId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        setAllowed(!!data.allow_support_impersonation);
        setCanEdit(!!data.can_edit);
      })
      .catch(() => {});
  }, [organizationId]);

  const change = async (next: boolean) => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await authFetch(`/api/impersonation/org-policy/${organizationId}`, {
        method: 'PUT',
        jsonBody: { allow_support_impersonation: next },
      });
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      setAllowed(!!data.allow_support_impersonation);
      setMessage(
        next
          ? 'Support can view as users in this organization when needed.'
          : `Support can no longer view as users here.${data.ended_sessions ? ` ${data.ended_sessions} open session(s) ended.` : ''}`,
      );
    } catch {
      setMessage('Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Eye className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Support "View as user"</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Lets a WeaveIn super admin open the app as one of your staff or portal members to troubleshoot, with a Jira
              ticket and a written reason. The person is emailed each time, every action is recorded, and messages,
              signatures, payments and password changes are always blocked.
            </p>
            {message && <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">{message}</p>}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {(allowed === null || saving) && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
          {allowed !== null && (
            <Switch
              checked={allowed}
              disabled={!canEdit || saving}
              onCheckedChange={(v: boolean) => void change(v)}
              aria-label="Allow support View as user"
            />
          )}
        </div>
      </div>
      {!canEdit && allowed !== null && (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Only an org admin can change this.</p>
      )}
    </div>
  );
}

export default SupportAccessOrgSetting;
